//Hooks
import { get } from 'svelte/store';
//Formating
import { format } from 'svelte-i18n';
//Helpers
import {
	isSupportedScheduleMethod,
	isSupportedVirtualMethod,
} from 'src/helpers/actions/schedules/supported/schedule_supported_helpers';
import { getLocalControlOptions } from '../core/local_action_helpers';
import { buildLightLabels, stringifyLightsAction } from './lights_local_actions_view';
import { buildCuryLabels, stringifyCuryAction } from './cury_local_actions_view';
import { buildTRVLabels, stringifyTRVAction } from './trv_local_actions_view';
import { buildVirtualLabels, stringifyVirtualLabel } from './virtual_local_actions_view';
import { buildRollerLabels, stringifyRollerAction } from './roller_local_actions_view';
import { buildCameraLabels, stringifyCameraAction } from './camera_local_actions_view';
import { buildPresenceLabels } from './presence_local_actions_view';
import { isCameraLocalActionMethod } from '../devices/camera_local_actions';
//Types
import type { Device } from 'src/types/Device';
import type { ActionJob } from 'src/types/requests/schedules/schedules_g2';
import type { LabelProps } from '@Shelly/shelly-design-system/index';

export const buildActionsLabels = (
	jobs: ActionJob[],
	deviceID: string,
	deviceChannels = 1,
): LabelProps[] => {
	const $format = get(format);
	let labelList: LabelProps[] = [];
	for (let i = 0; i < jobs.length; ++i) {
		const job: ActionJob = jobs[i];
		if (!job?.params) job.params = {};
		const method = (job?.method || '').toLowerCase();
		let channelInfo = '';
		if (
			(/^switch.(set|toggle)$/.test(method) && job.params?.id >= 100) ||
			(deviceChannels > 1 && job.params?.id)
		) {
			channelInfo = ` (${job.params.id})`;
		}
		if (/.toggle/i.test(method)) {
			labelList = [
				...labelList,
				{
					value:
						$format(`schedule_action_toggle`, {
							default: 'toggle',
						}) + channelInfo,
					icon: `fas fa-toggle-on`,
				},
			];
		} else if (/.setconfig$/i.test(method) && job.params?.config?.mode) {
			labelList = [
				...labelList,
				{
					value: $format(`local_action_set_mode`, {
						default: 'Set Mode property',
					}),
				},
				{
					value: job.params.config.mode.toUpperCase(),
				},
			];
		} else if (/^(switch|cb).set/.test(method) && job.params.on != null) {
			const state = job.params.on === true ? 'on' : 'off';
			const isOn = state === 'on';
			labelList = [
				...labelList,
				{
					value:
						$format(`schedule_action_${state}`, {
							default: state,
						}) + channelInfo,
					icon: isOn ? 'fa-duotone fa-light fa-toggle-on' : 'fas fa-toggle-off',
					...(isOn && {
						iconStyle:
							'--fa-primary-color: var(--color-success); --fa-secondary-color: var(--color-success-10); --fa-secondary-opacity: 1',
					}),
					...(!isOn && {
						iconColor: '--color-danger',
					}),
				},
			];
		} else if (job.params.output != null) {
			const output = job.params.output === true ? 'on' : 'off';
			const isOutputOn = output === 'on';
			labelList = [
				...labelList,
				{
					value:
						$format(`schedule_action_${output}`, {
							default: output,
						}) + channelInfo,
					icon: isOutputOn ? 'fa-duotone fa-light fa-toggle-on' : 'fas fa-toggle-off',
					...(isOutputOn && {
						iconStyle:
							'--fa-primary-color: var(--color-success); --fa-secondary-color: var(--color-success-10); --fa-secondary-opacity: 1',
					}),
					...(!isOutputOn && {
						iconColor: '--color-danger',
					}),
				},
			];
		}
		if (/cover/i.test(method)) {
			labelList = buildRollerLabels(method, job, labelList);
		} else if (/^(light|cct|rgbcct|rgbw?)/.test(method)) {
			labelList = buildLightLabels(job, labelList);
		} else if (/^(blu)?trv.(target|boost|position|ext_t)$/.test(method)) {
			labelList = buildTRVLabels(method, job, labelList);
		} else if (/^(cury).(boost|setconfig|set)$/.test(method)) {
			labelList = buildCuryLabels(method, job, labelList);
		} else if (/^(presencezone).(setconfig)$/.test(method)) {
			labelList = buildPresenceLabels(deviceID, method, job, labelList);
		} else if (/^media./i.test(method)) {
			labelList.push({
				icon: `far fa-${/.stop/i.test(method) ? 'stop' : /.pause/i.test(method) ? 'pause' : 'play'}`,
			});
			if (job.params?.volume >= 0 && job.params?.volume <= 100) {
				labelList.push({
					icon: `far fa-volume`,
					value: `${job.params.volume}%`,
				});
			}
		} else if (isCameraLocalActionMethod(method)) {
			labelList = buildCameraLabels(method, job, labelList);
		} else if (/^thermostat./i.test(method)) {
			labelList = [
				...labelList,
				{
					icon: 'fal fa-temperature-list',
				},
			];
		} else if (!isSupportedScheduleMethod(method)) {
			if (/^shelly\.update$/.test(method)) {
				labelList.push({
					value: $format(`schedule_firmware_update`, {
						default: 'firmware update',
					}),
					icon: 'fas fa-arrows-rotate',
				});
			} else if (/^script\.[a-z]+$/.test(method)) {
				labelList.push({
					value: method,
					icon: 'far fa-brackets-curly',
				});
			} else if (method) {
				labelList.push({
					value: method,
				});
			}
		} else if (isSupportedVirtualMethod(method)) {
			labelList = buildVirtualLabels(deviceID, job, labelList);
		}
		if (job.params.toggle_after >= 0) {
			labelList.push({
				value: `${job.params.toggle_after}s`,
				icon: 'fas fa-clock-rotate-left',
			});
		}
	}

	return labelList;
};

export const stringifyLocalAction = (action: ActionJob, device: Device): string => {
	let actionStringify = '';
	if (!isSupportedScheduleMethod(action?.method)) {
		actionStringify =
			action?.method +
			(action?.params?.id != null && /^script\./i.test(action?.method)
				? ` ID: ${action.params?.id}`
				: '');
		if (
			/^Shelly.Update/i.test(action?.method) &&
			action?.params?.stage &&
			action.params.stage != 'stable'
		) {
			actionStringify += ` (${action.params.stage})`;
		}
		return actionStringify;
	}
	const $format = get(format);
	const method = action.method.toLowerCase();
	const params = action.params;
	const controlOptions = getLocalControlOptions(action, device);
	const componentTitle = controlOptions.find((i) => i.value == method)?.title;
	if (componentTitle) {
		actionStringify = ` ${componentTitle} (${params.id})`;
	}
	if (isSupportedVirtualMethod(method)) {
		const label = stringifyVirtualLabel(action, device.id);
		const value = /^button.trigger$/.test(method) ? params.event : params.value;
		actionStringify = label
			? String(label.value)
			: ` ${method.replace('.set', '').replace('.', ' ')} (${params.id}): ${value || '?'} , `;
	} else if (method == 'switch.set') {
		actionStringify += ` ${$format('label_output_state', {
			default: 'Output State',
		})}: ${
			params.on == true
				? $format('on_action', { default: 'on' })
				: $format('off_action', { default: 'off' })
		} , `;
	} else if (method == 'cb.set') {
		actionStringify = ` ${$format('label_output_circuit_breaker', {
			default: 'Circuit Breaker',
		})}: ${
			params.output == true
				? $format('on_action', { default: 'on' })
				: $format('off_action', { default: 'off' })
		} , `;
	} else if (method == 'cover.gotoposition') {
		actionStringify += stringifyRollerAction(action);
	} else if (/^(rgbw?|light|cct|rgbcct)\.set$/.test(method)) {
		actionStringify = stringifyLightsAction(action);
	} else if (/^(blu)?trv.(target|boost|position|call|ext_t)$/.test(method)) {
		actionStringify = stringifyTRVAction(method, action, device);
	} else if (/^(cury).(boost|setconfig|set)$/.test(method)) {
		actionStringify = stringifyCuryAction(method, action);
	} else if (isCameraLocalActionMethod(method)) {
		actionStringify = stringifyCameraAction(method, action);
	}
	if (params.toggle_after != undefined) {
		actionStringify += `<i class="fas fa-clock-rotate-left" style="margin:0 5px;display:inline-block"></i> ${params.toggle_after}s , `;
	}
	return actionStringify.replace(/, $/, '');
};
